import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, Heart, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { SocialPost } from '@/types/team-posts';
import { PostEngagementModal } from '@/components/team-posts/PostEngagementModal';

export function RecentSocialPostCard() {
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);

  const { data: posts, isLoading } = useQuery({
    queryKey: ['dashboard-recent-social'],
    queryFn: async (): Promise<SocialPost[]> => {
      const { data, error } = await (supabase as any)
        .from('v_social_posts_ui_enhanced')
        .select('*')
        .not('post_date', 'is', null)
        .order('post_date', { ascending: false })
        .limit(5);
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const categoryColor = (cat: string | null) => {
    switch (cat?.toLowerCase()) {
      case 'competitor': return 'bg-red-100 text-red-700 border-red-200';
      case 'tech_provider': case 'technology': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'team': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Recent Social Posts</CardTitle>
            </div>
            <Link to="/team-posts">
              <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : !posts?.length ? (
            <p className="text-sm text-muted-foreground text-center py-6">No social posts yet</p>
          ) : (
            <div className="space-y-2">
              {posts.map((post) => {
                const name = post.poster_name || post.post_author || 'Unknown';
                const category = (post as any).author_category || (post as any).category;
                return (
                  <div
                    key={post.post_id}
                    className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedPost(post)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{name}</p>
                        {category && (
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${categoryColor(category)}`}>
                            {category.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {post.content_preview || post.full_content?.substring(0, 100) || 'No content'}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Heart className="h-3 w-3" />{post.likes || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />{post.comments || 0}
                        </span>
                        {post.post_date && (
                          <span>{formatDistanceToNow(new Date(post.post_date), { addSuffix: true })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="pt-3 text-center">
            <Link to="/team-posts" className="text-xs text-primary hover:underline font-medium">
              View All Posts →
            </Link>
          </div>
        </CardContent>
      </Card>

      <PostEngagementModal
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={(open) => { if (!open) setSelectedPost(null); }}
      />
    </>
  );
}
